import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';
import {
    allowedCount,
    buildSnapshot,
    calcForecast,
    formatDisplayDate,
    formatPct,
    outlookFor,
    rateTone,
    requiredDaily,
    STRATEGY_PRESETS,
    wholeCount,
} from './rfpaScrForecastUtils';
import { useSiteNames } from './useSiteCatalog';

// Option-year quarters (Apr–Mar), matching the PMF RFPA/SCR tracker:
// Q1 Apr–Jun, Q2 Jul–Sep, Q3 Oct–Dec, Q4 Jan–Mar.
const getOptionYearQuarter = (date) => {
    const month = date.getMonth();
    const year = date.getFullYear();
    if (month >= 3 && month <= 5) return { oy: year, q: 1, startMonth: 3 };
    if (month >= 6 && month <= 8) return { oy: year, q: 2, startMonth: 6 };
    if (month >= 9 && month <= 11) return { oy: year, q: 3, startMonth: 9 };
    return { oy: year - 1, q: 4, startMonth: 0 };
};

const quarterKey = ({ oy, q }) => `OY${oy}-Q${q}`;

const quarterLabel = ({ oy, q }) => {
    const ranges = {
        1: 'Apr–Jun',
        2: 'Jul–Sep',
        3: 'Oct–Dec',
        4: 'Jan–Mar',
    };
    return `OY ${oy} Q${q} (${ranges[q]})`;
};

const getCurrentQuarterKey = () => quarterKey(getOptionYearQuarter(new Date()));

const getQuarterDateRange = (key) => {
    if (!key) return null;
    const m = /^OY(\d{4})-Q([1-4])$/.exec(key);
    if (!m) return null;
    const oy = parseInt(m[1], 10);
    const q = parseInt(m[2], 10);
    const startMonthByQ = { 1: 3, 2: 6, 3: 9, 4: 0 };
    const startYear = q === 4 ? oy + 1 : oy;
    const startMonth = startMonthByQ[q];
    const start = new Date(startYear, startMonth, 1);
    const end = new Date(startYear, startMonth + 3, 0);
    return { start, end };
};

const extractList = (responseData) => {
    if (Array.isArray(responseData)) return responseData;
    if (responseData && Array.isArray(responseData.results)) return responseData.results;
    return [];
};

const parseDateOnly = (s) => {
    if (!s) return null;
    const str = String(s).slice(0, 10);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
    if (!m) return null;
    return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
};

const TONE_COLORS = {
    light: { good: '#2f7d5a', warn: '#b36b00', bad: '#b83b3b' },
    dark: { good: '#81c784', warn: '#ffb74d', bad: '#ef9a9a' },
};

const PILL_COLORS = {
    light: {
        good: { bg: '#dff3e8', fg: '#2f7d5a' },
        warn: { bg: '#fff0d0', fg: '#b36b00' },
        bad: { bg: '#fde3e3', fg: '#b83b3b' },
    },
    dark: {
        good: { bg: '#1b3d2a', fg: '#81c784' },
        warn: { bg: '#3d2e12', fg: '#ffb74d' },
        bad: { bg: '#3d1b1b', fg: '#ef9a9a' },
    },
};

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
        headerBg: isDark ? '#2a2a2a' : '#e9eef3',
        heroBg: isDark ? '#1a2a38' : '#eaf4fa',
        meterBg: isDark ? '#333' : '#e7edf1',
        meterFill: isDark ? '#4caf50' : '#2f7d5a',
        actionBg: isDark ? '#262626' : '#fafbfc',
        formulaBg: isDark ? '#2a2a2a' : '#f3f6f8',
    };

    return {
        page: { backgroundColor: colors.bg, minHeight: '100vh', padding: '20px', color: colors.text },
        headerCard: {
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: `0 2px 5px ${colors.shadow}`,
            border: `1px solid ${colors.border}`,
        },
        navLink: { color: colors.link, textDecoration: 'none', fontSize: '14px', display: 'inline-block', marginBottom: '10px' },
        title: { fontSize: '24px', fontWeight: 'bold', color: colors.text, margin: '0 0 6px 0' },
        subtitle: { fontSize: '14px', color: colors.subtleText, margin: 0 },
        section: {
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            boxShadow: `0 2px 5px ${colors.shadow}`,
            border: `1px solid ${colors.border}`,
            overflow: 'hidden',
            marginBottom: '20px',
        },
        sectionHeader: {
            padding: '15px 20px',
            borderBottom: `2px solid ${colors.border}`,
            backgroundColor: colors.headerBg,
        },
        sectionTitle: { fontSize: '18px', fontWeight: 'bold', color: colors.text, margin: 0 },
        sectionBody: { padding: '16px 20px' },
        controls: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '12px',
            padding: '12px 20px',
            borderBottom: `1px solid ${colors.border}`,
            alignItems: 'end',
        },
        fieldLabel: {
            display: 'block',
            fontSize: '12px',
            fontWeight: 700,
            color: colors.subtleText,
            marginBottom: '5px',
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
        },
        select: {
            width: '100%',
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
            fontSize: '14px',
        },
        input: {
            width: '100%',
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
            fontSize: '14px',
            boxSizing: 'border-box',
        },
        metricGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '13px',
            padding: '16px 20px',
        },
        metricCard: {
            backgroundColor: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            padding: '16px',
            boxShadow: `0 2px 5px ${colors.shadow}`,
        },
        metricLabel: {
            fontSize: '12px',
            color: colors.subtleText,
            fontWeight: 700,
            textTransform: 'uppercase',
        },
        metricValue: { fontSize: '28px', fontWeight: 800, marginTop: '4px' },
        metricSub: { fontSize: '12px', color: colors.subtleText, marginTop: '4px' },
        split: {
            display: 'grid',
            gridTemplateColumns: '1.15fr 0.85fr',
            gap: '16px',
            marginBottom: '20px',
        },
        hero: {
            display: 'flex',
            justifyContent: 'space-between',
            gap: '18px',
            alignItems: 'center',
            padding: '14px 16px',
            borderRadius: '10px',
            backgroundColor: colors.heroBg,
            marginBottom: '12px',
            flexWrap: 'wrap',
        },
        heroLabel: { fontSize: '13px', color: colors.subtleText },
        heroValue: { fontSize: '25px', fontWeight: 800, color: colors.text },
        meter: {
            height: '12px',
            backgroundColor: colors.meterBg,
            borderRadius: '99px',
            overflow: 'hidden',
            margin: '8px 0 16px',
        },
        meterFill: {
            display: 'block',
            height: '100%',
            backgroundColor: colors.meterFill,
        },
        pill: {
            display: 'inline-block',
            borderRadius: '99px',
            padding: '4px 9px',
            fontSize: '12px',
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
        },
        tableWrap: { overflowX: 'auto' },
        table: { width: '100%', borderCollapse: 'collapse', minWidth: '640px' },
        th: {
            padding: '10px 9px',
            textAlign: 'right',
            fontSize: '12px',
            textTransform: 'uppercase',
            color: colors.subtleText,
            backgroundColor: colors.headerBg,
            borderBottom: `1px solid ${colors.border}`,
        },
        thLeft: { textAlign: 'left' },
        td: {
            padding: '10px 9px',
            textAlign: 'right',
            fontSize: '13px',
            color: colors.text,
            borderBottom: `1px solid ${colors.border}`,
            fontVariantNumeric: 'tabular-nums',
        },
        tdLeft: { textAlign: 'left' },
        actions: { display: 'grid', gap: '9px' },
        action: {
            padding: '12px',
            border: `1px solid ${colors.border}`,
            borderRadius: '10px',
            backgroundColor: colors.actionBg,
            fontSize: '13px',
            color: colors.text,
        },
        actionTitle: { display: 'block', marginBottom: '3px', fontWeight: 700 },
        loadingBox: { padding: '40px', display: 'flex', justifyContent: 'center' },
        empty: { padding: '28px 20px', color: colors.subtleText, fontSize: '14px', lineHeight: 1.5 },
        foot: { color: colors.subtleText, fontSize: '12px', margin: '0 4px 8px' },
        formula: {
            fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace',
            backgroundColor: colors.formulaBg,
            padding: '2px 5px',
            borderRadius: '4px',
        },
        colors,
        isDark,
    };
};

function RfpaScrForecastPage({ theme, embedded = false }) {
    const styles = getStyles(theme);
    const siteNames = useSiteNames();
    const toneSet = TONE_COLORS[theme === 'dark' ? 'dark' : 'light'];
    const pillSet = PILL_COLORS[theme === 'dark' ? 'dark' : 'light'];

    const [siteFilter, setSiteFilter] = useState('');
    const [quarterFilter, setQuarterFilter] = useState(getCurrentQuarterKey);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    const [scenario, setScenario] = useState('.961');
    const [targetPct, setTargetPct] = useState(96.1);
    const [days, setDays] = useState(1);
    const [daysTouched, setDaysTouched] = useState(false);
    const [mix, setMix] = useState(0);

    const fetchData = useCallback(async () => {
        if (!siteFilter) {
            setRows([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const res = await api.get(`/rfpa-scr-daily/?site_name=${encodeURIComponent(siteFilter)}`);
            const list = extractList(res.data)
                .map((r) => ({ ...r, _date: parseDateOnly(r.date) }))
                .filter((r) => r._date)
                .sort((a, b) => a._date - b._date);
            setRows(list);
        } catch (err) {
            console.error('RFPA/SCR forecast fetch failed:', err);
            const status = err?.response?.status;
            setAlert({
                show: true,
                message: status === 404
                    ? 'RFPA SCR endpoint not found (backend may need a redeploy).'
                    : 'Failed to load RFPA/SCR forecast data.',
                type: 'error',
            });
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [siteFilter]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        if (!siteFilter && siteNames.length) {
            setSiteFilter(siteNames[0]);
        }
    }, [siteNames, siteFilter]);

    const quarterOptions = useMemo(() => {
        const seen = new Map();
        for (const r of rows) {
            const info = getOptionYearQuarter(r._date);
            const key = quarterKey(info);
            if (!seen.has(key)) {
                seen.set(key, { key, label: quarterLabel(info), oy: info.oy, q: info.q });
            }
        }
        return Array.from(seen.values()).sort((a, b) => {
            if (a.oy !== b.oy) return b.oy - a.oy;
            return b.q - a.q;
        });
    }, [rows]);

    useEffect(() => {
        if (loading || rows.length === 0) return;
        const keys = new Set(quarterOptions.map((q) => q.key));
        if (keys.has(quarterFilter)) return;
        const current = getCurrentQuarterKey();
        setQuarterFilter(keys.has(current) ? current : (quarterOptions[0]?.key || getCurrentQuarterKey()));
    }, [loading, rows.length, quarterOptions, quarterFilter]);

    const filteredRows = useMemo(() => {
        const range = getQuarterDateRange(quarterFilter);
        if (!range) return rows;
        return rows.filter((r) => r._date >= range.start && r._date <= range.end);
    }, [rows, quarterFilter]);

    const snapshot = useMemo(() => buildSnapshot(filteredRows), [filteredRows]);

    useEffect(() => {
        setDaysTouched(false);
    }, [siteFilter, quarterFilter]);

    useEffect(() => {
        if (!daysTouched) {
            setDays(Math.max(1, snapshot.remaining_days || 1));
        }
    }, [snapshot.remaining_days, daysTouched]);

    const target = (Number(targetPct) || 96.1) / 100;
    const forecastDays = Math.max(1, Number(days) || 1);
    const result = useMemo(
        () => calcForecast(snapshot, target, Number(mix), forecastDays),
        [snapshot, target, mix, forecastDays],
    );
    const outlook = useMemo(() => outlookFor(result), [result]);

    const selectedQuarterLabel = useMemo(() => {
        const found = quarterOptions.find((q) => q.key === quarterFilter);
        if (found) return found.label;
        const m = /^OY(\d{4})-Q([1-4])$/.exec(quarterFilter);
        if (!m) return quarterFilter;
        return quarterLabel({ oy: parseInt(m[1], 10), q: parseInt(m[2], 10) });
    }, [quarterFilter, quarterOptions]);

    const onScenarioChange = (value) => {
        setScenario(value);
        if (value !== 'custom') {
            setTargetPct(+(Number(value) * 100).toFixed(1));
        }
    };

    const onTargetChange = (value) => {
        setTargetPct(value);
        setScenario('custom');
    };

    const toneColor = (tone) => toneSet[tone] || styles.colors.text;
    const pillStyle = (key) => {
        const mapped = key === 'infeasible' ? 'bad' : key === 'tight' ? 'warn' : 'good';
        const colors = pillSet[mapped];
        return { ...styles.pill, backgroundColor: colors.bg, color: colors.fg };
    };

    const constraint = snapshot.rfpa < snapshot.scr
        ? 'RFPA is the weaker component; prioritize RFP-ready aircraft against TAR.'
        : 'SCR is the weaker component; protect executable schedule and sortie completion.';

    const comparisons = STRATEGY_PRESETS.map((preset) => {
        const r = calcForecast(snapshot, preset.target, 0, forecastDays);
        return { ...preset, result: r, outlook: outlookFor(r) };
    });

    const hasActuals = snapshot.actual_days > 0;

    return (
        <div style={embedded ? undefined : styles.page}>
            <CustomAlert {...alert} onClose={() => setAlert({ show: false })} theme={theme} />

            {!embedded && (
                <div style={styles.headerCard}>
                    <Link to="/" style={styles.navLink}>&larr; Back to Home</Link>
                    <h1 style={styles.title}>RFPA/SCR Forecast</h1>
                    <p style={styles.subtitle}>
                        Translate the quarter-end average target into daily RFPA and SCR operating requirements.
                    </p>
                </div>
            )}

            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <h2 style={styles.sectionTitle}>
                        RFPA/SCR Forecast — {siteFilter} — {selectedQuarterLabel}
                    </h2>
                </div>

                <div style={styles.controls}>
                    <div>
                        <label style={styles.fieldLabel}>Site</label>
                        <select value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)} style={styles.select}>
                            {siteNames.map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label style={styles.fieldLabel}>Quarter</label>
                        <select value={quarterFilter} onChange={(e) => setQuarterFilter(e.target.value)} style={styles.select}>
                            {quarterOptions.map((q) => (
                                <option key={q.key} value={q.key}>{q.label}</option>
                            ))}
                            {quarterOptions.length === 0 && (
                                <option value={quarterFilter}>{selectedQuarterLabel}</option>
                            )}
                        </select>
                    </div>
                    <div>
                        <label style={styles.fieldLabel}>Goal strategy</label>
                        <select value={scenario} onChange={(e) => onScenarioChange(e.target.value)} style={styles.select}>
                            <option value=".961">Break even · 96.1%</option>
                            <option value=".965">Buffer · 96.5%</option>
                            <option value=".97">Stretch · 97.0%</option>
                            <option value="custom">Custom</option>
                        </select>
                    </div>
                    <div>
                        <label style={styles.fieldLabel}>Target average</label>
                        <input
                            type="number"
                            min="50"
                            max="100"
                            step="0.1"
                            value={targetPct}
                            onChange={(e) => onTargetChange(e.target.value)}
                            style={styles.input}
                        />
                    </div>
                    <div>
                        <label style={styles.fieldLabel}>Remaining fly days</label>
                        <input
                            type="number"
                            min="1"
                            max="100"
                            step="1"
                            value={days}
                            onChange={(e) => {
                                setDaysTouched(true);
                                setDays(e.target.value);
                            }}
                            style={styles.input}
                        />
                    </div>
                    <div>
                        <label style={styles.fieldLabel}>RFPA / SCR emphasis</label>
                        <select value={String(mix)} onChange={(e) => setMix(Number(e.target.value))} style={styles.select}>
                            <option value="0">Balanced</option>
                            <option value="0.01">Protect RFPA (+1 pt)</option>
                            <option value="-0.01">Protect SCR (+1 pt)</option>
                        </select>
                    </div>
                </div>

                {loading ? (
                    <div style={styles.loadingBox}><Spinner /></div>
                ) : !hasActuals ? (
                    <div style={styles.empty}>
                        No actual RFPA/SCR days found for {siteFilter} in {selectedQuarterLabel}.
                        Enter DSR SCR-RFPA data or review the RFPA SCR tab, then return here to forecast remaining fly days.
                    </div>
                ) : (
                    <>
                        <div style={styles.metricGrid}>
                            <div style={styles.metricCard}>
                                <div style={styles.metricLabel}>Current average</div>
                                <div style={{ ...styles.metricValue, color: toneColor(rateTone(snapshot.avg, target)) }}>
                                    {formatPct(snapshot.avg)}
                                </div>
                                <div style={styles.metricSub}>
                                    {snapshot.actual_days} actual days through {formatDisplayDate(snapshot.last_actual)}
                                </div>
                            </div>
                            <div style={styles.metricCard}>
                                <div style={styles.metricLabel}>Current RFPA</div>
                                <div style={{ ...styles.metricValue, color: toneColor(rateTone(snapshot.rfpa, target)) }}>
                                    {formatPct(snapshot.rfpa)}
                                </div>
                                <div style={styles.metricSub}>
                                    RFP {wholeCount(snapshot.totals.rfp)} / TAR {wholeCount(snapshot.totals.tar)}
                                </div>
                            </div>
                            <div style={styles.metricCard}>
                                <div style={styles.metricLabel}>Current SCR</div>
                                <div style={{ ...styles.metricValue, color: toneColor(rateTone(snapshot.scr, target)) }}>
                                    {formatPct(snapshot.scr)}
                                </div>
                                <div style={styles.metricSub}>
                                    Sfl {wholeCount(snapshot.totals.sfl)} / adjusted schedule {wholeCount(snapshot.totals.sked - snapshot.totals.swx - snapshot.totals.sxo)}
                                </div>
                            </div>
                            <div style={styles.metricCard}>
                                <div style={styles.metricLabel}>Target average</div>
                                <div style={{ ...styles.metricValue, color: toneColor(result.feasible ? 'good' : 'bad') }}>
                                    {formatPct(target)}
                                </div>
                                <div style={styles.metricSub}>{forecastDays} forecast fly days</div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {!loading && hasActuals && (
                <>
                    <div style={styles.split} className="rfpa-forecast-split">
                        <div style={{ ...styles.section, marginBottom: 0, borderLeft: `5px solid ${styles.colors.link}` }}>
                            <div style={styles.sectionHeader}>
                                <h2 style={styles.sectionTitle}>Required daily operating plan</h2>
                            </div>
                            <div style={styles.sectionBody}>
                                <div style={styles.hero}>
                                    <div>
                                        <div style={styles.heroLabel}>
                                            Required average on remaining days to finish at {formatPct(target)}
                                        </div>
                                        <div style={styles.heroValue}>{formatPct(outlook.requiredFuture)}</div>
                                    </div>
                                    <span style={pillStyle(outlook.key)}>{outlook.label}</span>
                                </div>
                                <div style={styles.meter}>
                                    <div style={{ ...styles.meterFill, width: `${Math.min(100, outlook.requiredFuture * 100)}%` }} />
                                </div>
                                <div style={styles.tableWrap}>
                                    <table style={{ ...styles.table, minWidth: 0 }}>
                                        <thead>
                                            <tr>
                                                <th style={{ ...styles.th, ...styles.thLeft }} />
                                                <th style={styles.th}>Minimum whole-number daily plan</th>
                                                <th style={styles.th}>Required future rate</th>
                                                <th style={styles.th}>Quarter-end goal</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td style={{ ...styles.td, ...styles.tdLeft }}>RFPA</td>
                                                <td style={styles.td}>
                                                    <strong>{requiredDaily(result.rfpDay)} RFP</strong>
                                                    {' '}against about {wholeCount(result.futureTar / forecastDays)} TAR
                                                </td>
                                                <td style={styles.td}>{formatPct(result.futureRfpa)}</td>
                                                <td style={styles.td}>{formatPct(result.rfpaGoal)}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ ...styles.td, ...styles.tdLeft }}>SCR</td>
                                                <td style={styles.td}>
                                                    <strong>{requiredDaily(result.sflDay)} completed sorties</strong>
                                                    {' '}against about {wholeCount(result.futureAvail / forecastDays)} adjusted schedule
                                                </td>
                                                <td style={styles.td}>{formatPct(result.futureScr)}</td>
                                                <td style={styles.td}>{formatPct(result.scrGoal)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div style={{ ...styles.section, marginBottom: 0 }}>
                            <div style={styles.sectionHeader}>
                                <h2 style={styles.sectionTitle}>Recommended actions</h2>
                            </div>
                            <div style={{ ...styles.sectionBody, ...styles.actions }}>
                                <div style={styles.action}>
                                    <b style={styles.actionTitle}>1 · Protect the constraint</b>
                                    {constraint}
                                </div>
                                <div style={styles.action}>
                                    <b style={styles.actionTitle}>2 · Whole-quarter control limits</b>
                                    Across the {forecastDays} remaining days, the mathematical allowance is no more than{' '}
                                    <strong>{allowedCount(result.rfpMiss)} total RFPA misses</strong> and{' '}
                                    <strong>{allowedCount(result.scrGap)} total unflown adjusted sorties.</strong>
                                </div>
                                <div style={styles.action}>
                                    <b style={styles.actionTitle}>3 · Build a buffer</b>
                                    Run to 96.5% when feasible; it absorbs late-quarter variance instead of merely touching 96.1%.
                                </div>
                                <div style={styles.action}>
                                    <b style={styles.actionTitle}>4 · Reforecast every day</b>
                                    Refresh DSR SCR-RFPA entries and act on whichever component has the larger required future rate.
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={styles.section}>
                        <div style={styles.sectionHeader}>
                            <h2 style={styles.sectionTitle}>Strategy comparison</h2>
                        </div>
                        <div style={styles.tableWrap}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={{ ...styles.th, ...styles.thLeft }}>Strategy</th>
                                        <th style={styles.th}>Target Avg</th>
                                        <th style={styles.th}>Final RFPA</th>
                                        <th style={styles.th}>Final SCR</th>
                                        <th style={styles.th}>Minimum RFP / day</th>
                                        <th style={styles.th}>Minimum sorties / day</th>
                                        <th style={styles.th}>Outlook</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {comparisons.map((row) => (
                                        <tr key={row.name}>
                                            <td style={{ ...styles.td, ...styles.tdLeft }}>{row.name}</td>
                                            <td style={styles.td}>{formatPct(row.target)}</td>
                                            <td style={styles.td}>{formatPct(row.result.rfpaGoal)}</td>
                                            <td style={styles.td}>{formatPct(row.result.scrGoal)}</td>
                                            <td style={styles.td}>{requiredDaily(row.result.rfpDay)}</td>
                                            <td style={styles.td}>{requiredDaily(row.result.sflDay)}</td>
                                            <td style={styles.td}>
                                                <span style={pillStyle(row.outlook.key)}>{row.outlook.label}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <p style={styles.foot}>
                        Model logic matches the tracker:{' '}
                        <span style={styles.formula}>RFPA = RFP / TAR</span>,{' '}
                        <span style={styles.formula}>SCR = Sfl / (Sked − Swx − Sxo)</span>, and{' '}
                        <span style={styles.formula}>AVG = (RFPA + SCR) / 2</span>.
                        Rates are capped at 100%. Required aircraft and sortie counts are rounded up to whole numbers
                        so the displayed plan does not understate the target. Remaining fly days default to Projected
                        tracker rows for the selected quarter.
                    </p>
                </>
            )}

            <style>
                {`@media (max-width: 900px) {
                    .rfpa-forecast-split { grid-template-columns: 1fr !important; }
                }`}
            </style>
        </div>
    );
}

export default RfpaScrForecastPage;
